import * as React from "react";
import { Box, Table, TableBody, TableCell, TableHead, TableRow, useTheme, alpha, useMediaQuery } from "@mui/material";
import { useTitle } from "../../hooks/useTitle";
import { useGetInvoicesQuery } from "../../services/invoice.service";
import { CardBase, GlassTable } from "../../components/global";
import InvoiceTableRow from "../../components/finances/InvoiceTableRow";
import InvoicePaginationControls from "../../components/finances/InvoicePaginationControls";

const InvoicesPage = () => {
  useTitle("Company Invoices");
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [startingAfter, setStartingAfter] = React.useState("");

  const { data } = useGetInvoicesQuery({
    startingAfter,
  });

  const hasMore = React.useMemo(() => data?.has_more, [data]);

  const headerGradient = isDark
    ? `linear-gradient(135deg, ${alpha("#12903C", 0.2)} 0%, ${alpha("#12903C", 0.1)} 100%)`
    : `linear-gradient(135deg, ${alpha("#12903C", 0.15)} 0%, ${alpha("#12903C", 0.08)} 100%)`;

  const borderColor = isDark
    ? alpha(theme.palette.divider, 0.3)
    : alpha(theme.palette.divider, 0.5);

  const rowHoverBg = isDark
    ? alpha(theme.palette.primary.main, 0.08)
    : alpha(theme.palette.primary.main, 0.04);

  const headCellSx = {
    fontWeight: 700,
    textTransform: "uppercase",
    fontSize: { xs: "0.65rem", sm: "0.75rem" },
    letterSpacing: "0.08em",
    borderBottom: "none",
    color: "#12903C",
    py: { xs: 1.5, sm: 2 },
    px: { xs: 1, sm: 2 },
  } as const;

  const loadFirstPage = () => {
    setStartingAfter("");
  };

  const loadNextPage = () => {
    if (data?.data && data?.data.length > 0) {
      setStartingAfter(data?.data[data?.data.length - 1].id);
    }
  };

  return (
    <Box sx={{ width: "auto", px: { xs: 0, sm: 3 }, py: 3 }}>
      <CardBase
        title="Company Invoices"
        useGlassCard={true}
        nested={false}
        actions={
          <InvoicePaginationControls
            hasMore={hasMore || false}
            hasStartingAfter={!!startingAfter}
            onFirstPage={loadFirstPage}
            onNextPage={loadNextPage}
          />
        }
      >
        <Box sx={{ p: { xs: 1.5, sm: 2.5, md: 3 }, overflowX: "auto" }}>
          <GlassTable maxHeight={{ xs: 420, md: 560 }}>
            <Table
              stickyHeader
              sx={{
                minWidth: { xs: 600, sm: 650 },
                width: "100%",
                "& .MuiTableHead-root .MuiTableRow-root": {
                  background: headerGradient,
                },
                "& .MuiTableHead-root .MuiTableCell-root": {
                  ...headCellSx,
                },
                "& .MuiTableBody-root .MuiTableCell-root": {
                  borderBottom: `1px solid ${borderColor}`,
                  fontSize: { xs: "0.8rem", sm: "0.95rem" },
                  color: theme.palette.text.primary,
                  py: { xs: 1.5, sm: 2 },
                  px: { xs: 1, sm: 2 },
                  verticalAlign: "top",
                  whiteSpace: { xs: "nowrap", sm: "normal" },
                },
                "& .MuiTableBody-root .MuiTableRow-root:hover": {
                  backgroundColor: rowHoverBg,
                },
              }}
              aria-label="invoices table"
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: { xs: 100, sm: 120 } }}>Number</TableCell>
                  <TableCell sx={{ minWidth: { xs: 100, sm: 120 } }}>Created</TableCell>
                  <TableCell sx={{ minWidth: { xs: 100, sm: 120 } }}>Due Date</TableCell>
                  <TableCell sx={{ minWidth: { xs: 80, sm: 100 } }}>Currency</TableCell>
                  <TableCell align="right" sx={{ minWidth: { xs: 90, sm: 110 } }}>Total</TableCell>
                  <TableCell sx={{ minWidth: { xs: 90, sm: 110 } }}>Status</TableCell>
                  <TableCell sx={{ minWidth: { xs: 100, sm: 120 } }}>&nbsp;</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.data?.map((invoice) => (
                  <InvoiceTableRow key={invoice.number} invoice={invoice} isMobile={isMobile} />
                ))}
              </TableBody>
            </Table>
          </GlassTable>
        </Box>
      </CardBase>
    </Box>
  );
};
export default InvoicesPage;
